// Office (Location) Form Modal — Add / Edit
// modal-lg — has address, city, state, country, phone, email, timezone fields

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

export interface OfficeFormData {
  code: string;
  name: string;
  aliasName: string;
  address: string;
  cityName: string;
  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  postalCode: string;
  phone: string;
  email: string;
  timeZone: string;
  description: string;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface OfficeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: OfficeFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: OfficeFormData) => void;
  onClose: () => void;
  // Lookup options
  countryOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  timezoneOptions: { value: string; label: string }[];
  onCountryChange?: (countryId: string) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const INITIAL_FORM_DATA: OfficeFormData = {
  code: '', name: '', aliasName: '', address: '',
  cityName: '', countryId: '', countryName: '',
  stateId: '', stateName: '',
  postalCode: '', phone: '', email: '', timeZone: '',
  description: '', statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CODE_PATTERN = /^[A-Z0-9_-]+$/;

export const OfficeFormModal: React.FC<OfficeFormModalProps> = ({
  isOpen, mode, initialData, isSaving = false, apiError, onSave, onClose,
  countryOptions, stateOptions, timezoneOptions, onCountryChange,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState<OfficeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors((p) => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const src = initialDataRef.current;
    const data: OfficeFormData = src ? { ...src } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose, isSaving]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const fd = formDataRef.current;
    const cm = modeRef.current;
    const ci = createInstanceIdRef.current;
    const modalId = cm === 'create'
      ? `${tabId}-form-modal-create-${ci || 'default'}`
      : `${tabId}-form-modal-edit-${fd.code || 'new'}`;
    const idx = ci || '1';
    const title = cm === 'create'
      ? (fd.code?.trim() ? `Add - ${fd.code.trim()} (${idx})` : fd.name?.trim() ? `Add - ${fd.name.trim()} (${idx})` : `Add New (${idx})`)
      : `Edit - ${fd.code || fd.name || 'Office'}`;
    const saved = { ...fd };
    const snap = initialSnapshotRef.current;
    const dirty = snap ? JSON.stringify(fd) !== snap : false;
    minimize({ id: modalId, title, moduleName, tabId, isDirty: dirty, onRestore: () => {
      isRestoreRef.current = true; setFormData(saved); initialSnapshotRef.current = snap;
      if (onRestoreModal) onRestoreModal(cm, cm === 'create' ? ci : undefined, cm !== 'create' ? fd.code : undefined);
    }});
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    if (!code) errors.code = 'Code is required';
    else if (code.length > 30) errors.code = 'Code must be 30 characters or less';
    else if (!CODE_PATTERN.test(code)) errors.code = 'Code: uppercase letters, numbers, hyphens, underscores only';
    if (!formData.name.trim()) errors.name = 'Location name is required';
    else if (formData.name.length > 150) errors.name = 'Name must be 150 characters or less';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLInputElement>('input,textarea')?.focus(); }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({ ...formData, code: formData.code.trim().toUpperCase(), name: formData.name.trim() });
  }, [formData, onSave]);

  const setField = useCallback((field: keyof OfficeFormData, value: string) => {
    clearFieldError(field);
    setFormData((p) => ({ ...p, [field]: value }));
  }, [clearFieldError]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add New Location' : 'Edit Location'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (<button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}><IconSystem.chevronDown size={16} /></button>)}
            <button className="modal-close" onClick={handleClose} disabled={isSaving} title="Close"><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {apiError && (
            <div style={{ marginBottom: '0.75rem', padding: '0.625rem 0.75rem', background: 'rgba(220,38,38,0.08)', border: '1px solid #dc2626', borderRadius: '4px', color: '#dc2626', fontSize: '0.8125rem', lineHeight: 1.4 }}>{apiError}</div>
          )}

          {/* Row 1: Code + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input type="text" className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`} value={formData.code} maxLength={30} placeholder="e.g. MUM" disabled={isSaving || mode === 'edit'} onChange={(e) => setField('code', e.target.value.toUpperCase())} />
              {getFieldError('code') && <div className="form-error-message">{getFieldError('code')}</div>}
            </div>
            <div className="form-group" data-field="statusCode">
              <SyutSelect label="Status" value={formData.statusCode} options={STATUS_OPTIONS} isClearable={false} size="sm" onChange={(val) => setFormData((p) => ({ ...p, statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE' }))} />
            </div>
          </div>

          {/* Location Name */}
          <div className="form-group" data-field="name">
            <label className="form-label">Location Name <span className="text-danger">*</span></label>
            <input type="text" className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`} value={formData.name} maxLength={150} placeholder="e.g. Mumbai Head Office" disabled={isSaving} onChange={(e) => setField('name', e.target.value)} />
            {getFieldError('name') && <div className="form-error-message">{getFieldError('name')}</div>}
          </div>

          {/* Address */}
          <div className="form-group" data-field="address">
            <label className="form-label">Address</label>
            <textarea className="form-input" value={formData.address} rows={2} placeholder="Street address" disabled={isSaving} onChange={(e) => setField('address', e.target.value)} style={{ resize: 'vertical' }} />
          </div>

          {/* Country + State (dropdowns) */}
          <div className="form-grid-2">
            <div className="form-group" data-field="countryId">
              <SyutSelect
                label="Country"
                value={formData.countryId}
                options={countryOptions}
                isClearable
                size="sm"
                placeholder="Select country..."
                onChange={(val) => {
                  const newCountryId = (val as string) || '';
                  // Find country name for the cached display field
                  const opt = countryOptions.find((o) => o.value === newCountryId);
                  setFormData((p) => ({
                    ...p,
                    countryId: newCountryId,
                    countryName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                    // Clear state when country changes
                    stateId: '',
                    stateName: '',
                  }));
                  onCountryChange?.(newCountryId);
                }}
              />
            </div>
            <div className="form-group" data-field="stateId">
              <SyutSelect
                label="State / Province"
                value={formData.stateId}
                options={stateOptions}
                isClearable
                size="sm"
                placeholder={formData.countryId ? 'Select state...' : 'Select country first'}
                onChange={(val) => {
                  const newStateId = (val as string) || '';
                  const opt = stateOptions.find((o) => o.value === newStateId);
                  setFormData((p) => ({
                    ...p,
                    stateId: newStateId,
                    stateName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                  }));
                }}
              />
            </div>
          </div>

          {/* City + Postal Code */}
          <div className="form-grid-2">
            <div className="form-group" data-field="cityName">
              <label className="form-label">City</label>
              <input type="text" className="form-input" value={formData.cityName} maxLength={100} placeholder="e.g. Mumbai" disabled={isSaving} onChange={(e) => setField('cityName', e.target.value)} />
            </div>
            <div className="form-group" data-field="postalCode">
              <label className="form-label">Postal Code</label>
              <input type="text" className="form-input" value={formData.postalCode} maxLength={20} placeholder="e.g. 400001" disabled={isSaving} onChange={(e) => setField('postalCode', e.target.value)} />
            </div>
          </div>

          {/* Timezone */}
          <div className="form-group" data-field="timeZone">
            <SyutSelect
              label="Timezone"
              value={formData.timeZone}
              options={timezoneOptions}
              isClearable
              size="sm"
              placeholder="Select timezone..."
              onChange={(val) => {
                setFormData((p) => ({ ...p, timeZone: (val as string) || '' }));
              }}
            />
          </div>

          {/* Phone + Email */}
          <div className="form-grid-2">
            <div className="form-group" data-field="phone">
              <label className="form-label">Phone</label>
              <input type="text" className="form-input" value={formData.phone} maxLength={30} placeholder="e.g. +91-22-12345678" disabled={isSaving} onChange={(e) => setField('phone', e.target.value)} />
            </div>
            <div className="form-group" data-field="email">
              <label className="form-label">Email</label>
              <input type="text" className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`} value={formData.email} maxLength={200} placeholder="e.g. mumbai@firm.com" disabled={isSaving} onChange={(e) => setField('email', e.target.value)} />
              {getFieldError('email') && <div className="form-error-message">{getFieldError('email')}</div>}
            </div>
          </div>

          {/* Description */}
          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea className="form-input" value={formData.description} rows={2} placeholder="Optional description" disabled={isSaving} onChange={(e) => setField('description', e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>{mode === 'create' ? 'Create' : 'Save Changes'}</button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen} title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'} message={mode === 'create' ? `Create location "${formData.code.trim().toUpperCase()}"?` : `Save changes to "${formData.code}"?`} confirmLabel={mode === 'create' ? 'Create' : 'Save'} cancelLabel="Cancel" variant="default" onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?" confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger" onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default OfficeFormModal;
